import theme from "../../themes";
import { formatSecondsToTime } from "../../utils/formatDecimalNumber";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import styled from "styled-components";
import { getCallStatusLabel } from "../../constants/callStatus";
import { ReactComponent as ArrowUpIcon } from "../../assets/icons/arrowUp.svg";
import { ReactComponent as ArrowDownIcon } from "../../assets/icons/arrowDown.svg";
import {
  formatActionTitle,
  formatTimeSlot,
  normalizeActionValue,
  resolveCallActionKeys,
  type ReportActionKey,
} from "./reportActions";

export type { ReportActionKey };
export {
  MUST_HAVE_CALL_ACTION_KEYS,
  collectTakenActionKeys,
  formatActionTitle,
  formatActionValueForCsv,
  formatCallSummaryForCsv,
  mergeActionKeys,
  resolveCallActionKeys,
} from "./reportActions";

dayjs.extend(utc);

interface CallTimeSort {
  order: "ascend" | "descend";
  onToggle: () => void;
}

export const generateDynamicColumns = (
  sampleData: any[],
  t: any,
  onShowSummaryDetail?: (record: any) => void,
  callTimeSort?: CallTimeSort,
  extraActionKeys?: ReportActionKey[]
) => {
  const baseColumns = [
    {
      title: t("reports.tableColumns.recipientName"),
      dataIndex: "recipient_name",
      key: "recipient_name",
      render: (text: string) => <NameText>{text || "—"}</NameText>,
    },
    {
      title: t("reports.tableColumns.phoneNumber"),
      dataIndex: "phone",
      key: "phone",
      render: (text: string) => text || "—",
    },
    {
      title: t("reports.tableColumns.duration"),
      dataIndex: "duration",
      key: "duration",
      render: (duration: number) => formatSecondsToTime(duration),
    },
    {
      title: t("reports.tableColumns.status") || t("status"),
      dataIndex: "status",
      key: "status",
      render: (status: string) => {
        const label = getCallStatusLabel(status);
        if (!status || label === "-") return "—";
        return <StatusPill $colors={callStatusColors(status)}>{label}</StatusPill>;
      },
    },
    {
      title: callTimeSort ? (
        <SortLabel onClick={callTimeSort.onToggle}>
          {t("reports.tableColumns.callTime")}
          {callTimeSort.order === "ascend" ? <ArrowUpIcon /> : <ArrowDownIcon />}
        </SortLabel>
      ) : (
        t("reports.tableColumns.callTime")
      ),
      dataIndex: "start_time",
      key: "call_time",
      render: (callTime: string) =>
        callTime ? dayjs(callTime).format("h:mm A, MMM D, YYYY") : "—",
    },
  ];

  const summaryColumn = {
    title: t("reports.tableColumns.summary"),
    dataIndex: "call_analysis",
    key: "summary",
    width: 300,
    render: (callAnalysis: any, record: any) => {
      const summary = callAnalysis?.summary;
      const followUp = callAnalysis?.follow_up;
      const reason = callAnalysis?.reason;
      const followUpRecommendation =
        typeof followUp === "string" ? followUp : followUp?.recommendation;

      const hasInline = Boolean(summary);
      const hasDetail = Boolean(reason || followUpRecommendation);

      const handleShowDetail = () => {
        if (!hasDetail && !hasInline) return;
        if (onShowSummaryDetail) {
          onShowSummaryDetail(record);
        }
      };

      if (!hasInline && !hasDetail) {
        return <SummaryText>{record?.summary || "—"}</SummaryText>;
      }

      return (
        <SummaryBlock>
          <SummaryText>{summary || record?.summary || "—"}</SummaryText>
          {hasDetail ? (
            <DetailLink type="button" onClick={handleShowDetail}>
              {t("common.detail") || "Detail"}
            </DetailLink>
          ) : null}
        </SummaryBlock>
      );
    },
  };

  const resolvedActionKeys = resolveCallActionKeys(sampleData, extraActionKeys);
  if (resolvedActionKeys.length === 0) return [...baseColumns, summaryColumn];

  const actionColumns = resolvedActionKeys.map(({ key }) => {
    return {
      title: formatActionTitle(key),
      dataIndex: ["action", key],
      key: key,
      render: (actionData: any) => {
        if (
          !actionData?.value ||
          (Array.isArray(actionData.value) && actionData.value.length === 0)
        ) {
          return "—";
        }

        const value = actionData.value;
        const items = Array.isArray(value) ? value : [value];

        const renderItem = (item: any) => {
          if (item && typeof item === "object") {
            const keys = Object.keys(item);
            const hasTime = Boolean(item.start_time || item.end_time);
            const otherKeys = hasTime
              ? keys.filter((k) => k !== "start_time" && k !== "end_time")
              : keys;
            return (
              <ActionValue>
                {hasTime ? <div>{formatTimeSlot(item)}</div> : null}
                {otherKeys.map((k) => (
                  <div key={k}>{normalizeActionValue(item[k])}</div>
                ))}
              </ActionValue>
            );
          }
          return <div>{normalizeActionValue(item)}</div>;
        };

        return (
          <div>
            {items.map((it: any, index: number) => (
              <div
                key={index}
                style={{
                  marginBottom: index < items.length - 1 ? "8px" : "0",
                  lineHeight: "1.4",
                }}
              >
                {renderItem(it)}
              </div>
            ))}
          </div>
        );
      },
    };
  });

  return [...baseColumns, ...actionColumns, summaryColumn];
};

const callStatusColors = (status: string) => {
  const key = String(status || "").toLowerCase();
  if (key === "completed" || key === "answered") {
    return { text: theme.colors.purple1, background: theme.colors.purple2 };
  }
  if (key === "failed" || key === "rejected") {
    return { text: "#B42318", background: "#FEE4E2" };
  }
  if (key === "busy" || key === "voicemail") {
    return { text: theme.colors.yellow4, background: theme.colors.yellow3 };
  }
  return { text: "#374151", background: theme.colors.grey5 };
};

const SortLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;

  svg {
    display: block;
  }
`;

const NameText = styled.span`
  color: #1890ff;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
`;

const StatusPill = styled.span<{
  $colors: { text: string; background: string };
}>`
  display: inline-flex;
  align-items: center;
  background: ${({ $colors }) => $colors.background};
  color: ${({ $colors }) => $colors.text};
  border-radius: 12px;
  padding: 5px 12px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  white-space: nowrap;
`;

const SummaryBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  max-width: 280px;
`;

const SummaryText = styled.span`
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-weight: 400;
  color: #111827;
`;

const DetailLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  color: #1890ff;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  cursor: pointer;
`;

const ActionValue = styled.div`
  font-weight: 400;
  color: #111827;
  line-height: 20px;
`;
